import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verify2FA, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [challengeToken, setChallengeToken] = useState(null);
  const [otpCode, setOtpCode] = useState('');

  if (isAuthenticated) {
    navigate(location.state?.from || '/', { replace: true });
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return setError('Please enter your email address.');
    if (!password) return setError('Please enter your password.');
    setLoading(true);
    try {
      const result = await login({ email: email.trim(), password });
      if (result.requires2FA) {
        setChallengeToken(result.challengeToken);
      } else {
        navigate(location.state?.from || '/', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Could not sign you in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    if (!otpCode.trim()) return setError('Please enter the 6-digit code from your authenticator app.');
    setLoading(true);
    try {
      await verify2FA({ challengeToken, code: otpCode.trim() });
      navigate(location.state?.from || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'That code did not match. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (challengeToken) {
    return (
      <div className="max-w-xl mx-auto py-4 fade-in">
        <div className="bg-white rounded-3xl border-2 border-stone-200 shadow-sm p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-primary/10 rounded-full mx-auto mb-4 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2">Enter your code</h1>
            <p className="text-text-muted text-lg">
              Open your authenticator app and enter the 6-digit code for Bellarine Tech Assist.
            </p>
          </div>
          <form onSubmit={onVerifyOtp} className="space-y-5">
            <div>
              <label className="block text-base font-semibold text-text mb-2">Authentication code</label>
              <input
                data-testid="otp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                className="w-full py-4 text-2xl text-center tracking-widest font-mono rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="123 456"
                maxLength={10}
              />
            </div>
            {error && (
              <div data-testid="otp-error" className="bg-error/10 text-error border border-error/30 rounded-xl p-4 text-base">{error}</div>
            )}
            <Button type="submit" disabled={loading} data-testid="otp-submit" className="w-full bg-primary text-white hover:bg-primary-600 rounded-xl py-6 text-lg font-bold">
              {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
              Verify and sign in
            </Button>
          </form>
          <div className="mt-6 pt-6 border-t border-stone-200 text-center">
            <button
              type="button"
              onClick={() => { setChallengeToken(null); setOtpCode(''); setPassword(''); }}
              className="text-primary font-semibold hover:underline"
              data-testid="back-to-password"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-4 fade-in">
      <div className="bg-white rounded-3xl border-2 border-stone-200 shadow-sm p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <LogIn className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2">
            Welcome back
          </h1>
          <p className="text-text-muted text-lg">
            Sign in to continue your course.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-base font-semibold text-text mb-2">Email address</label>
            <div className="relative">
              <Mail className="w-5 h-5 text-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-base font-semibold text-text">Password</label>
              <Link to="/forgot-password" className="text-sm text-primary font-semibold hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Lock className="w-5 h-5 text-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-12 pr-14 py-4 text-lg rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="Your password"
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-text-muted hover:text-text"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="bg-error/10 text-error border border-error/30 rounded-xl p-4 text-base">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-primary text-white hover:bg-primary-600 rounded-xl py-6 text-lg font-bold"
          >
            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
            Sign in
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-stone-200 text-center">
          <p className="text-text-muted text-base">
            New here?{' '}
            <Link to="/signup" className="text-primary font-semibold hover:underline" data-testid="go-to-signup">
              Create a free account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
